var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase */
import { TweenMax, TweenLite } from 'gsap';
import { createAudio } from 'utils/audio-util';
import CommonAudio from 'mixins/CommonAudio';
import ErrorLogger from 'mixins/ErrorLogger';
import AssetLoader from 'mixins/AssetLoader';
import InactivityHelper from 'mixins/InactivityHelper';
import MetaHelper from 'mixins/MetaHelper';
import AccessibilityHelper from './AccessibilityHelper';
import { activity, keyboard, student, activityTyping, application, mapState, mapGetters, mapMutations, mapActions } from '../store/helpers';
import mixins from 'vue-typed-mixins';
const ActivityManagerBase = () => mixins(CommonAudio, ErrorLogger, AssetLoader, InactivityHelper, MetaHelper, AccessibilityHelper).extend({
    data() {
        return {
            sfx: undefined,
            sfxDescriptions: undefined,
            started: false,
            congrats: null
        };
    },
    computed: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, mapState(activity, [
        'progress',
        'viewedIntro',
        'isRestarting',
        'showIntro',
        'instructions'
    ])), mapState(activity, {
        roundsUntyped: 'rounds'
    })), mapState(student, ['accessibilitySettings'])), { rounds() {
            return this.roundsUntyped;
        } }), mapState(activity, {
        activityData: state => state.data,
        // @ts-ignore TODO(kevin): where is spotCheck coming from?
        isSpotCheck: state => Boolean(state.data.spotCheck),
        instructionsAudio: state => state.instructionsAudio,
        instructionsAudios: state => state.instructionsAudios,
        voiceInstructionsAudio: state => { var _a, _b; return (_b = (_a = state.instructions) === null || _a === void 0 ? void 0 : _a.audio) === null || _b === void 0 ? void 0 : _b.src; },
        voiceInstructionsAudioDescription: state => { var _a; return (_a = state.instructions) === null || _a === void 0 ? void 0 : _a.text; }
    })), mapGetters(activity, [
        'currentStep',
        'totalStepsInRound',
        'isLastStepInRound',
        'isLastRound',
        'isEndOfActivity',
        'isSpotCheckActivity',
        'showAssessmentAcceleratorIntro'
    ])), mapGetters(activity, {
        currentRoundUntyped: 'currentRound'
    })), { currentRound() {
            return this.currentRoundUntyped;
        } }), mapState(keyboard, {
        showKeyboardAtStart: state => state.settings.showKeyboardAtStart
    })), mapState(application, ['kwtMode', 'device'])), mapState(application, {
        isChromeOS: state => state.device.isChromeOS,
        userDevice: state => state.device
    })), mapGetters(application, ['ipadWithoutExternalKeyboard', 'usingExternalKeyboard'])), { isAssessmentAcceleratorTesterActivity() {
            return !!(this.isAssessmentAcceleratorActivity() && ['tester'].includes(this.kwtMode));
        },
        isAssessmentAcceleratorPreviewActivity() {
            return !!(this.isAssessmentAcceleratorActivity() &&
                ['preview', 'previewLite'].includes(this.kwtMode));
        },
        showActivityNextSectionPortal() {
            return (this.viewedIntro ||
                (!this.isAssessmentAcceleratorPreviewActivity &&
                    (!this.showAssessmentAcceleratorIntro || this.isAssessmentAcceleratorTesterActivity)));
        } }),
    watch: {
        viewedIntro(newViewed, oldViewed) {
            if (newViewed && !oldViewed && !this.started) {
                this.handleStart();
            }
        },
        instructions(newValue, oldValue) {
            if ((oldValue === null || oldValue === void 0 ? void 0 : oldValue.text) && newValue.text !== oldValue.text) {
                this.playInstructions();
            }
        },
        isRestarting(newValue, oldValue) {
            if (newValue && !oldValue) {
                TweenMax.to('.activity-stage', 0.5, { css: { autoAlpha: 1 } });
            }
        },
        accessibilitySettings(newVal) {
            var _a, _b;
            const newVolume = newVal.audioSettings.muteSounds ? 0 : newVal.audioSettings.volume * 0.01;
            const audioInstructions = this.instructionsAudio;
            const instructionAudios = this.instructionsAudios;
            audioInstructions === null || audioInstructions === void 0 ? void 0 : audioInstructions.volume(newVolume);
            if (instructionAudios === null || instructionAudios === void 0 ? void 0 : instructionAudios.length) {
                instructionAudios === null || instructionAudios === void 0 ? void 0 : instructionAudios.forEach(audio => {
                    audio === null || audio === void 0 ? void 0 : audio.volume(newVolume);
                });
            }
            (_a = this.updateAllAudioVolumeInCache) === null || _a === void 0 ? void 0 : _a.call(this);
            // @ts-ignore implemented by consumer
            const loadSoundsMethod = (_b = this.updateSoundsBasedOnSettings) !== null && _b !== void 0 ? _b : this.loadSounds;
            // @ts-ignore implemented by consumer
            loadSoundsMethod === null || loadSoundsMethod === void 0 ? void 0 : loadSoundsMethod();
        }
    },
    mounted() {
        window.addEventListener('resize', this.setActivityContainerHeight);
    },
    destroyed() {
        window.removeEventListener('resize', this.setActivityContainerHeight);
    },
    created() {
        return __awaiter(this, void 0, void 0, function* () {
            this.resetErrorLogger();
            // @ts-ignore implemented by consumer
            if (this.loadActivity) {
                // @ts-ignore implemented by consumer
                yield this.loadActivity();
            }
            // @ts-ignore implemented by consumer
            if (this.setupKeyboard) {
                // @ts-ignore implemented by consumer
                this.setupKeyboard();
            }
            this.completeActivitySetup();
            if (!this.showIntro || this.isAssessmentAcceleratorTesterActivity) {
                this.setToolbarMode('inActivity');
                this.$nextTick(() => this.setIntroViewed());
            }
            if (this.isAssessmentAcceleratorPreviewActivity) {
                this.setToolbarMode('fullScreenOnly');
            }
            if (this.viewedIntro &&
                this.isAssessmentAcceleratorActivity() &&
                this.device.isMobile &&
                !this.started) {
                this.handleStart();
            }
        });
    },
    methods: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, mapMutations(activity, [
        'keepInstructionsOnScreen',
        'resetActivityStore',
        'createRounds',
        'setInstructions',
        'setInstructionsAudio',
        'setInstructionsAudios',
        'incrementStep',
        'completeActivitySetup',
        'completeStep',
        'resetProgress',
        'resetViewedIntro',
        'resetCompletedSteps',
        'jumpToRound',
        'overwriteRoundSteps',
        'overwriteProgressStep',
        'setIntroViewed'
    ])), mapGetters(activity, ['isAssessmentAcceleratorActivity'])), mapMutations(activityTyping, [
        'resetAttemptedKey',
        'overwriteProgressText',
        'setFreeTextTyping'
    ])), mapMutations(keyboard, [
        'showKeyboard',
        'hideKeyboard',
        'releaseHoldKey',
        'resetKeyboard'
    ])), mapActions(activity, ['restartActivity'])), mapMutations(application, ['setKwtMode', 'setToolbarMode'])), { playInstructions() {
            if (!this.voiceInstructionsAudio)
                return;
            const audioInstructions = createAudio(this.voiceInstructionsAudio, {
                html5: !this.device.isMobile,
                volume: this.accessibilitySettings.audioSettings.muteSounds
                    ? 0
                    : this.accessibilitySettings.audioSettings.volume * 0.01
            });
            this.setInstructionsAudio(audioInstructions);
            if (this.voiceInstructionsAudioDescription)
                this.setScreenReaderAlert(this.voiceInstructionsAudioDescription);
            const voiceIntro = TweenLite.to('.activity-instructions', 0, {
                delay: 1,
                onComplete: () => { var _a; return (_a = this.instructionsAudio) === null || _a === void 0 ? void 0 : _a.play(); }
            });
            // @ts-ignore TODO(kevin): add Howler types
            window.Howler.ctx.resume();
            if (this.isAssessmentAcceleratorActivity() && this.sfx)
                this.sfx['INSTRUCTIONS'] = audioInstructions;
            return voiceIntro;
        },
        handleWrongKey(attempt) {
            // @ts-ignore implemented by consumer
            this.logError(attempt.character, this.expectedKey);
            this.sndError();
        },
        handleStart() {
            this.started = true;
            if (this.showKeyboardAtStart) {
                this.showKeyboard();
            }
            this.startActivityOrRound();
            this.playInstructions();
            if (this.isActiveAssessment) {
                this.startTimer();
            }
        },
        startActivityOrRound() {
            this.resetErrorLogger();
            this.errorLoggerStart();
            // @ts-ignore implemented by consumer
            if (this.startActivity) {
                // @ts-ignore implemented by consumer
                this.startActivity();
                // @ts-ignore implemented by consumer
            }
            else if (this.startRound) {
                // @ts-ignore implemented by consumer
                this.startRound();
            }
        },
        playStepSound(sfx) {
            const { roundIndex, stepIndex } = this.progress;
            sfx.soundsPerRound[roundIndex][stepIndex].play();
        },
        nextStep({ isFirstStep = false, enableKeyboard = true, stepCallback = null } = {}) {
            // @ts-ignore implemented by consumer
            if (enableKeyboard && this.enableKeyboard) {
                // @ts-ignore implemented by consumer
                this.enableKeyboard();
            }
            // @ts-ignore implemented by consumer
            this.setCurrentStep({ isFirstStep, stepCallback });
        },
        playCongrats() {
            var _a, _b, _c;
            const congratsAudio = (_a = this.congrats) !== null && _a !== void 0 ? _a : (_c = (_b = this.activityData) === null || _b === void 0 ? void 0 : _b.affirmationAudio) === null || _c === void 0 ? void 0 : _c.src;
            if (!congratsAudio)
                return;
            const audioInstructions = createAudio(congratsAudio, {
                html5: !this.device.isMobile,
                volume: this.accessibilitySettings.audioSettings.muteSounds
                    ? 0
                    : this.accessibilitySettings.audioSettings.volume * 0.01
            });
            audioInstructions === null || audioInstructions === void 0 ? void 0 : audioInstructions.play();
            return audioInstructions;
        },
        completeActivity(playCongrats = true) {
            if (playCongrats) {
                this.playCongrats();
            }
            this.processEndOfActivity();
        },
        processEndOfActivity() {
            this.errorLoggerStop();
            this.resetAttemptedKey();
            if (this.hideKeyboard) {
                this.hideKeyboard();
            }
            // @ts-ignore implemented by consumer
            if (this.isTouchHold) {
                this.releaseHoldKey();
            }
            TweenMax.to('.activity-stage', 2, {
                autoAlpha: 0,
                onComplete: () => {
                    var _a, _b;
                    const isPreview = this.kwtMode === 'preview' || this.kwtMode === 'previewLite';
                    if (this.kwtMode === 'curriculum') {
                        if (this.isSpotCheck) {
                            this.$router.replace({ path: '/spotcheck-result', params: {} });
                            this.setAppTitle(`${this.weekText()} - ${this.activityAsnText()} - Spot Check Completion Page - ${(_a = this.activityTitle) === null || _a === void 0 ? void 0 : _a.display}`);
                        }
                        else {
                            this.$router.replace({ path: '/activity-trophy', params: {} });
                            this.setAppTitle(`${this.weekText()} - ${this.activityAsnText()} - Activity Completion Page`);
                        }
                    }
                    else if (isPreview && this.isSpotCheckActivity) {
                        this.$router.replace({ path: '/spotcheck-result', params: {} });
                        this.setAppTitle(`Spot Check Completion Page - ${(_b = this.activityTitle) === null || _b === void 0 ? void 0 : _b.display}`);
                    }
                    else {
                        this.handleModeRedirect();
                    }
                }
            });
        },
        handleModeRedirect() {
            if (this.kwtMode !== 'curriculum') {
                this.resetProgress();
                this.resetViewedIntro();
            }
            switch (this.kwtMode) {
                case 'tester':
                    this.resetActivityStore();
                    this.resetKeyboard();
                    this.$router.push({ path: '/tester' });
                    break;
                case 'preview':
                case 'previewLite':
                    this.restartActivity(false);
                    break;
                default:
                    this.setKwtMode('unknown');
                    this.resetActivityStore();
                    this.resetKeyboard();
                    this.$router.push({ path: '/tester' });
            }
        },
        loadAAAudios(audiosMap) {
            return __awaiter(this, void 0, void 0, function* () {
                const soundPaths = Object.keys(audiosMap).map(key => audiosMap[key]);
                this.queueAudio(soundPaths);
                const cache = yield this.downloadAudio();
                const cachedAudiosMap = {};
                Object.keys(audiosMap).forEach(key => (cachedAudiosMap[key] = cache.get(audiosMap[key])));
                return cachedAudiosMap;
            });
        },
        playAudio(audioKey) {
            this.stopAllAudio();
            return new Promise((resolve, reject) => {
                var _a, _b;
                if (!((_a = this.sfx) === null || _a === void 0 ? void 0 : _a[audioKey]))
                    return reject(new Error('non-existent sfx object or audio key'));
                if ((_b = this.sfxDescriptions) === null || _b === void 0 ? void 0 : _b[audioKey])
                    this.setScreenReaderAlert(this.sfxDescriptions[audioKey]);
                this.sfx[audioKey].once('end', () => resolve());
                this.sfx[audioKey].once('playerror', () => reject());
                this.sfx[audioKey].play();
            });
        },
        stopAllAudio() {
            if (this.sfx)
                Object.keys(this.sfx).map(key => { var _a; return (_a = this.sfx) === null || _a === void 0 ? void 0 : _a[key].stop(); });
        },
        createAudio(filename) {
            return createAudio(filename, {
                html5: !this.device.isMobile,
                volume: this.getVolume()
            });
        },
        setActivityContainerHeight() {
            const activityContainer = document.querySelector('.activity-stage');
            const keyboardContainer = document.querySelector('.phoenix-keyboard-wrapper');
            const mainContainer = document.querySelector('.main-container');
            const makeSpaceForProgressBar = this.isAssessmentAcceleratorActivity() && this.device.isMobile;
            if (activityContainer && mainContainer) {
                activityContainer.style.height = `calc(${mainContainer.clientHeight}px - ${keyboardContainer ? keyboardContainer.clientHeight : 0}px - ${makeSpaceForProgressBar ? '70px' : '0px'})`;
            }
        } })
});
const ActivityManager = ActivityManagerBase();
export const ActivityManagerTyped = ActivityManagerBase;
export default ActivityManager;
